/**
 * Predictive Search, Custom Element <predictive-search>
 *
 * GOAT Search UX, Follows Shopify's official pattern:
 * https://shopify.dev/docs/storefronts/themes/navigation-search/search/predictive-search
 *
 * Features:
 * - Fetches results from Predictive Search API → Section Rendering API
 * - 300ms debounce
 * - Result caching synced across instances
 * - Recent searches (localStorage)
 * - Zero-click ATC from search results
 * - ARIA announcements
 */
const RECENT_STORAGE_KEY = 'vijvercentrum:recent_searches';
const MAX_RECENT = 5;

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.abortController = null;
    this.cachedResults = {};
  }

  connectedCallback() {
    // Defer setup, connectedCallback fires before children are parsed.
    // Try immediately, then defer with increasing delays as fallback.
    if (!this._trySetup()) {
      setTimeout(() => {
        if (!this._trySetup()) {
          setTimeout(() => this._trySetup(), 100);
        }
      }, 0);
    }
  }

  _trySetup() {
    if (this._initialized) return true;
    const input = this.querySelector('[data-search-input]');
    const results = this.querySelector('[data-search-results]');
    if (!input || !results) return false;
    this._initialized = true;
    this._setup(input, results);
    return true;
  }

  _setup(input, results) {
    this.input = input;
    this.results = results;
    this.inner = this.querySelector('[data-search-results-inner]');
    this.trending = this.querySelector('[data-search-trending]');
    // Only use overlay when inside the main header, prevents hero search from
    // triggering the header's full-screen backdrop (which would cover the hero results).
    const parentHeader = this.closest('header');
    this.overlay = parentHeader ? parentHeader.querySelector('[data-search-overlay]') : null;
    this.clearBtn = this.querySelector('[data-search-clear]');
    this.loading = this.querySelector('[data-search-loading]');
    this.liveRegion = document.getElementById('AjaxLiveRegion');

    if (!this.input || !this.results) return;

    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('input', () => this.onInput());
    this.clearBtn?.addEventListener('click', () => this.onClear());
    this.overlay?.addEventListener('click', () => this.close());

    // Form submit, save recent search
    this.querySelector('form')?.addEventListener('submit', (e) => {
      const q = this.input.value.trim();
      if (q.length < 2) {
        e.preventDefault();
      } else {
        this.saveRecentSearch(q);
      }
    });

    // Close on outside click (only for non-dialog instances)
    if (!this.closest('dialog')) {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('predictive-search') && !e.target.closest('[data-search-overlay]')) this.close();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.close(); this.input.blur(); }
    });

    // Bind clear-recent button
    this.querySelector('[data-clear-recent]')?.addEventListener('click', () => {
      this.clearRecentSearches();
    });
  }

  /* ─── Open / Close ─── */

  open() {
    this.overlay?.classList.remove('hidden');
    this.results.classList.remove('hidden');
    // Close mega menus when header search opens (prevents visual conflict)
    if (this.closest('header')) {
      document.querySelectorAll('.mega-panel.mega-active').forEach(p => p.classList.remove('mega-active'));
      document.querySelectorAll('[data-mega-trigger].mega-active').forEach(t => t.classList.remove('mega-active'));
    }
  }

  close() {
    this.overlay?.classList.add('hidden');
    this.results.classList.add('hidden');
    this.trending?.classList.add('hidden');
    this.loading?.classList.add('hidden');
  }

  /* ─── Events ─── */

  onFocus() {
    if (document.documentElement.classList.contains('doofinder-active')) return; // Doofinder neemt over
    this.open();
    if (this.input.value.trim().length < 2) {
      this.renderZeroState();
    } else {
      this.trending?.classList.add('hidden');
    }
  }

  onInput() {
    if (document.documentElement.classList.contains('doofinder-active')) return; // Doofinder neemt over
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    this.clearBtn?.classList.toggle('hidden', q.length === 0);

    if (q.length < 2) {
      if (this.inner) this.inner.innerHTML = '';
      this.renderZeroState();
      this.loading?.classList.add('hidden');
      return;
    }

    this.trending?.classList.add('hidden');
    this.timer = setTimeout(() => this.fetchResults(q), 300);
  }

  onClear() {
    this.input.value = '';
    this.input.focus();
    if (this.inner) this.inner.innerHTML = '';
    this.clearBtn?.classList.add('hidden');
    this.renderZeroState();
    this.loading?.classList.add('hidden');
  }

  /* ─── Zero-State: Recent Searches + Trending ─── */

  renderZeroState() {
    this.trending?.classList.remove('hidden');
    if (this.inner) this.inner.innerHTML = '';

    // Render recent searches from localStorage
    const recentContainer = this.querySelector('[data-search-recent]');
    const recentList = this.querySelector('[data-recent-list]');
    if (recentContainer && recentList) {
      const recent = this.getRecentSearches();
      if (recent.length > 0) {
        recentContainer.classList.remove('hidden');
        recentList.innerHTML = recent.map(term =>
          `<a href="/search?q=${encodeURIComponent(term)}" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-navy bg-slate-100 rounded-full no-underline hover:bg-aqua/10 hover:text-aqua-donker transition-all group">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-slate-300 shrink-0" aria-hidden="true"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/></svg>
            ${term}
          </a>`
        ).join('');
      } else {
        recentContainer.classList.add('hidden');
      }
    }
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY)) || [];
    } catch { return []; }
  }

  saveRecentSearch(term) {
    try {
      let recent = this.getRecentSearches();
      // Remove duplicate, add to front, cap at MAX_RECENT
      recent = recent.filter(t => t.toLowerCase() !== term.toLowerCase());
      recent.unshift(term);
      recent = recent.slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recent));
    } catch { /* localStorage not available */ }
  }

  clearRecentSearches() {
    try { localStorage.removeItem(RECENT_STORAGE_KEY); } catch { }
    const recentContainer = this.querySelector('[data-search-recent]');
    if (recentContainer) recentContainer.classList.add('hidden');
  }

  /* ─── Fetch Results (Section Rendering API) ─── */

  async fetchResults(query) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    // Check cache
    const cacheKey = query.toLowerCase().replace(/\s+/g, '-');
    if (this.cachedResults[cacheKey]) {
      this.renderResults(this.cachedResults[cacheKey]);
      return;
    }

    this.open();
    this.loading?.classList.remove('hidden');
    this.trending?.classList.add('hidden');
    if (this.inner) this.inner.innerHTML = '';

    try {
      const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(query)}&resources[type]=product,article,collection,query&resources[limit]=4&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,tag,vendor&section_id=predictive-search`;
      const res = await fetch(url, { signal: this.abortController.signal });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      this.loading?.classList.add('hidden');

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newInner = doc.querySelector('[data-search-results-inner]');

      if (newInner?.innerHTML.trim()) {
        this.cachedResults[cacheKey] = newInner.innerHTML;
        // Sync cache across all instances
        document.querySelectorAll('predictive-search').forEach((instance) => {
          if (instance !== this) instance.cachedResults[cacheKey] = newInner.innerHTML;
        });
        this.renderResults(newInner.innerHTML);
      } else {
        // No results, show friendly fallback
        if (this.inner) {
          this.inner.innerHTML = `<div class="p-6 text-center">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="mx-auto text-slate-200 mb-3" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <p class="text-sm text-slate-500 m-0 mb-1">Geen resultaten voor "<strong>${query}</strong>"</p>
            <p class="text-xs text-slate-500 m-0">Probeer een andere zoekterm of bekijk onze populaire producten</p>
          </div>`;
          // Show trending as fallback under the no-results message
          this.trending?.classList.remove('hidden');
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.loading?.classList.add('hidden');
      if (this.inner) {
        this.inner.innerHTML = '<div class="p-6 text-center text-sm text-slate-500">Er ging iets mis.</div>';
      }
    }
  }

  renderResults(html) {
    if (this.inner) {
      this.inner.innerHTML = html;
      this.bindATC();
    }
    this.open();
  }

  /* ─── Zero-Click Commerce: ATC from Search Results ─── */

  bindATC() {
    this.querySelectorAll('[data-search-atc]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const variantId = btn.dataset.searchAtc;
        const productTitle = btn.dataset.productTitle || '';
        btn.disabled = true;
        const originalHTML = btn.innerHTML;
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="animate-spin" aria-hidden="true"><path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93"/></svg>';

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
          });

          btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
          btn.classList.replace('bg-koi', 'bg-green-500');

          if (this.liveRegion) {
            this.liveRegion.textContent = `${productTitle} toegevoegd aan winkelwagen`;
          }

          // Refresh cart drawer + open it
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          if (window.CartDrawer) window.CartDrawer.open();

          setTimeout(() => {
            btn.innerHTML = originalHTML;
            btn.classList.replace('bg-green-500', 'bg-koi');
            btn.disabled = false;
          }, 2000);
        } catch (err) {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }
      });
    });
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
